import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  appColors,
  BackrsIcon,
  onEnterKeyPress,
  scrollToTopOfPage,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
  SchoolLevel,
} from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { addUsersSchoolLevel } from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';

import { Box, SxProps, Theme, Button } from '@mui/material';

import { Question } from './molecules/Question';
import {
  determineIfNextIsDisabled,
  getNextQuestionIndex,
  schoolLevelQuestionProps,
} from './survey.util';

const BUTTON_TEXT = appStrings.common.button;

type SurveyPageProps = Readonly<{
  careerQuestions: CareerQuestion[];
  userResponseSet: CareerUserResponseSet;
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  onFinishSurvey: () => void;
}>;

export const SurveyPage = ({
  careerQuestions,
  userResponseSet,
  addUserQuestionResponse,
  onFinishSurvey,
}: SurveyPageProps) => {
  const [hasSeenFirstQuestionFact, setHasSeenFirstQuestionFact] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  /** This state is used to toggle between school level question and career questions
   *
   * We need to track the school level separately, but want to use the same styling for displaying the question
   *
   */
  const [isOnSchoolLevelQuestion, setIsOnSchoolLevelQuestion] = useState(true);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentQuestionId = careerQuestions[currentQuestionIndex]?.id;

  const responsesForCurrentQuestion = userResponseSet.user_responses.find(
    (response) => response.career_question_id === currentQuestionId,
  );

  const handlePrevious = () => {
    if (!isOnSchoolLevelQuestion && currentQuestionIndex === 0) {
      // if we are past the school level question and on the first career question, we should go back to the school level question
      setIsOnSchoolLevelQuestion(true);

      return;
    }

    if (currentQuestionIndex === 0 && isOnSchoolLevelQuestion) {
      // if we are on the school level question, we should go back to the intro page
      navigate(APP_ROUTE.INTRO, { state: { startAtEnd: true } });
    }

    const previousIndex = getNextQuestionIndex({
      index: currentQuestionIndex,
      direction: 'BACKWARD',
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions: careerQuestions,
    });

    scrollToTopOfPage();

    setCurrentQuestionIndex(previousIndex);
  };

  const currentSchoolLevel = userResponseSet?.school_level;

  const handleSelectSchoolLevel = (schoolLevel: SchoolLevel) => {
    dispatch(addUsersSchoolLevel(schoolLevel));
  };

  const isNextDisabled = determineIfNextIsDisabled({
    currentQuestionIndex,
    userResponseSet,
    careerQuestions,
    isOnSchoolLevelQuestion,
    currentSchoolLevel,
  });

  const handleNext = () => {
    if (currentSchoolLevel && currentQuestionIndex === 0 && isOnSchoolLevelQuestion) {
      // if we are on the school level question and have a school level selected, we should move to the first career question
      setIsOnSchoolLevelQuestion(false);

      return;
    }

    if (isNextDisabled) {
      return;
    }

    if (currentQuestionIndex === careerQuestions.length - 1) {
      onFinishSurvey();

      return;
    }

    scrollToTopOfPage();

    const nextIndex = getNextQuestionIndex({
      index: currentQuestionIndex,
      direction: 'FORWARD',
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions: careerQuestions,
    });

    setCurrentQuestionIndex(nextIndex);
  };

  const activeQuestionView = isOnSchoolLevelQuestion ? (
    <Question
      {...schoolLevelQuestionProps({
        userResponseSet,
        careerQuestions,
        currentSchoolLevel,
        handleSelectSchoolLevel,
      })}
    />
  ) : (
    <Question
      currentQuestionIndex={currentQuestionIndex + 1}
      question={careerQuestions[currentQuestionIndex]}
      totalLengthOfSurvey={careerQuestions.length}
      responsesForQuestion={responsesForCurrentQuestion}
      userResponseSet={userResponseSet}
      addUserQuestionResponse={addUserQuestionResponse}
      isOnSchoolLevelQuestion={isOnSchoolLevelQuestion}
      hasSeenFirstQuestionFact={hasSeenFirstQuestionFact}
      setHasSeenFirstQuestionFact={setHasSeenFirstQuestionFact}
    />
  );

  return (
    <Box sx={containerStyle} tabIndex={0} onKeyDown={(event) => onEnterKeyPress(event, handleNext)}>
      <Box sx={questionAndActionContainerStyle}>
        {/* CURRENT QUESTION VIEW */}
        {activeQuestionView}

        {/* NAV BUTTONS */}
        <Box sx={buttonContainerStyle}>
          <Button variant="contained" sx={buttonStyle} onClick={handlePrevious}>
            <BackrsIcon name="IconLineArrowLeft" alt={BUTTON_TEXT.back} height={22} width={22} />
            {BUTTON_TEXT.back}
          </Button>
          <Button
            variant="contained"
            sx={buttonStyle}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {BUTTON_TEXT.next}
            <BackrsIcon name="IconLineArrowRight" alt={BUTTON_TEXT.next} height={22} width={22} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexGrow: 1,
  width: '100%',
  pt: { xs: 4, md: 10 },
  '@media (max-height: 768px)': {
    pt: 3,
  },
  pb: { xs: 2, md: 0 },
  px: 2,
  boxSizing: 'border-box',
  pl: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  ml: 1,
} as const;

const questionAndActionContainerStyle: SxProps<Theme> = {
  maxWidth: '633px',
  minWidth: '633px',
  '@media (max-width: 768px)': {
    maxWidth: 'unset',
    minWidth: '100%',
  },
} as const;

const buttonStyle: SxProps<Theme> = {
  marginTop: { xs: '.5rem', sm: '2rem' },
  // smaller screen height target
  '@media screen and (min-width: 768px) and (max-width: 1366px) and (max-height: 700px)': {
    marginTop: '2rem',
  },
  // Mobile phone specific styling
  '@media screen and (max-width: 600px)': {
    marginTop: '1.5rem',
  },
  borderRadius: '2rem',
  backgroundColor: appColors.color.blue1,
  paddingX: '1rem',
  paddingY: '.8rem',
  textTransform: 'none',
  fontWeight: 'bold',
  gap: 1.5,
  fontSize: {
    xs: '14px',
    sm: '16px',
  },
  lineHeight: 1,
} as const;

const buttonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
} as const;
